import React from "react";
import { ArrowRight, Check } from "lucide-react";
import { BrowserFrame } from "./Frames";
import { SERVICES, MEDIA } from "../mock";
import { Script } from "./Script";

const CheckoutMock = () => (
  <div className="rounded-2xl bg-white border border-black/[0.07] p-5 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.35)]">
    <div className="flex items-center justify-between">
      <p className="font-semibold text-[#0e0e0d]">Pekárna Zrno</p>
      <p className="text-xs text-[#8b8b84]">Košík (3)</p>
    </div>
    <div className="flex items-center justify-between mt-4 pb-4 border-b border-black/[0.07]">
      <p className="text-sm text-[#5f5f59]">Celkem</p>
      <p className="text-xl font-semibold text-[#0e0e0d]">4 695 Kč</p>
    </div>
    <div className="mt-4 space-y-2">
      <div className="h-9 rounded-lg bg-[#f4f4f1] flex items-center px-3 text-xs text-[#8b8b84]">
        8275 1947 4237 5739
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="h-9 rounded-lg bg-[#f4f4f1] flex items-center px-3 text-xs text-[#8b8b84]">10 / 32</div>
        <div className="h-9 rounded-lg bg-[#f4f4f1] flex items-center px-3 text-xs text-[#8b8b84]">123</div>
      </div>
    </div>
    <div className="mt-4 h-10 rounded-full bg-[#d8ff00] grid place-items-center text-sm font-semibold text-[#0e0e0d]">
      Zaplatit 4 695 Kč
    </div>
  </div>
);

const ChartMock = () => (
  <div className="rounded-2xl bg-white border border-black/[0.07] p-5">
    <div className="flex items-baseline justify-between">
      <p className="eyebrow text-[#8b8b84]">Konverze</p>
      <p className="text-sm font-semibold text-[#0e0e0d]">4,8 %</p>
    </div>
    <div className="mt-5 flex items-end gap-1.5 h-28">
      {[22, 30, 26, 41, 38, 52, 47, 66, 74, 92].map((h, i) => (
        <span
          key={i}
          className={`flex-1 rounded-[4px] ${i > 6 ? "bg-[#d8ff00]" : "bg-[#e6e6e0]"}`}
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
    <div className="flex justify-between mt-3 text-[10px] text-[#a5a59d]">
      <span>před webem</span>
      <span>po spuštění</span>
    </div>
  </div>
);

const SpeedMock = () => (
  <div className="rounded-2xl bg-white border border-black/[0.07] p-5">
    <p className="eyebrow text-[#8b8b84]">Core Web Vitals</p>
    <div className="mt-5 grid grid-cols-3 gap-3">
      {[
        ["LCP", "0,7 s"],
        ["CLS", "0,01"],
        ["INP", "38 ms"],
      ].map(([k, v]) => (
        <div key={k} className="text-center">
          <div className="relative mx-auto w-14 h-14">
            <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#ecece6" strokeWidth="3.5" />
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke="#d8ff00"
                strokeWidth="3.5"
                strokeDasharray="97.4"
                strokeDashoffset="8"
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 grid place-items-center text-[10px] font-semibold text-[#0e0e0d]">
              {k}
            </span>
          </div>
          <p className="text-xs text-[#5f5f59] mt-2">{v}</p>
        </div>
      ))}
    </div>
  </div>
);

const StatusMock = () => (
  <div className="rounded-2xl bg-white border border-black/[0.07] p-5 space-y-3">
    {[
      ["Dostupnost webu", "100 %"],
      ["Poslední záloha", "dnes 03:00"],
      ["Aktualizace systému", "hotovo"],
      ["Bezpečnostní scan", "čistý"],
    ].map(([k, v]) => (
      <div key={k} className="flex items-center gap-3">
        <span className="grid place-items-center w-6 h-6 rounded-full bg-[#d8ff00] text-[#0e0e0d] shrink-0">
          <Check size={13} strokeWidth={3} />
        </span>
        <p className="text-sm text-[#0e0e0d] flex-1">{k}</p>
        <p className="text-xs text-[#8b8b84]">{v}</p>
      </div>
    ))}
  </div>
);

const VISUALS = {
  browser: () => (
    <BrowserFrame media={MEDIA.serviceSite} host={MEDIA.serviceHost} mediaClass="aspect-[16/9] object-cover" />
  ),
  checkout: CheckoutMock,
  chart: ChartMock,
  speed: SpeedMock,
  status: StatusMock,
};

const SPANS = {
  firemni: "lg:col-span-4",
  eshop: "lg:col-span-2",
  landing: "lg:col-span-2",
  seo: "lg:col-span-2",
  udrzba: "lg:col-span-2",
};

export default function Services() {
  const go = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="sluzby" className="bg-white pb-24 md:pb-32" data-testid="services-section">
      <div className="container-x">
        <div className="reveal max-w-3xl">
          <p className="eyebrow text-[#8b8b84]">Služby</p>
          <h2 className="display text-[#0e0e0d] text-4xl sm:text-5xl lg:text-6xl mt-4">
            Všechno, co web potřebuje, aby{" "}
            <Script color="#0e0e0d" className="text-5xl sm:text-6xl lg:text-7xl mx-2">
              fungoval.
            </Script>
          </h2>
        </div>

        <div className="mt-20 grid lg:grid-cols-6 gap-5">
          {SERVICES.map((s) => {
            const Visual = VISUALS[s.kind];
            const wide = SPANS[s.id] === "lg:col-span-4";
            if (wide) {
              return (
                <article
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="reveal tile relative overflow-hidden p-7 md:p-9 lg:col-span-4 lg:min-h-[480px]"
                >
                  <div className="lg:max-w-[46%]">
                    <p className="eyebrow text-[#8b8b84]">{s.tag}</p>
                    <h3 className="display text-2xl md:text-[30px] text-[#0e0e0d] mt-3">{s.title}</h3>
                    <p className="text-[#5f5f59] mt-3 text-sm md:text-base leading-relaxed">{s.desc}</p>
                    <button
                      onClick={() => go("#kontakt")}
                      data-testid={`service-cta-${s.id}`}
                      className="underline-link mt-5 inline-flex items-center gap-2 text-[#0e0e0d] text-sm"
                    >
                      Nezávazná poptávka <ArrowRight size={15} />
                    </button>
                  </div>
                  <div className="mt-8 lg:mt-0 lg:absolute lg:-right-[7%] lg:-bottom-[9%] lg:w-[62%] rotate-[-2deg]">
                    <Visual />
                  </div>
                </article>
              );
            }
            return (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className={`reveal tile p-7 md:p-9 ${SPANS[s.id]}`}
              >
                <p className="eyebrow text-[#8b8b84]">{s.tag}</p>
                <h3 className="display text-2xl md:text-[28px] text-[#0e0e0d] mt-3">{s.title}</h3>
                <p className="text-[#5f5f59] mt-3 text-sm md:text-base leading-relaxed">{s.desc}</p>
                <button
                  onClick={() => go("#kontakt")}
                  data-testid={`service-cta-${s.id}`}
                  className="underline-link mt-5 inline-flex items-center gap-2 text-[#0e0e0d] text-sm"
                >
                  Nezávazná poptávka <ArrowRight size={15} />
                </button>
                <div className="mt-8">
                  <Visual />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
